// Global ⌘K / Ctrl+K jumper. Rolled by hand — no cmdk / radix, since our
// route corpus is tiny and static. Opens on hotkey from any page, filters
// the route list by query, closes on Escape or backdrop click.
//
// Trigger lives in App.tsx as a top-level keydown listener. This component
// just renders the overlay when `open` is true.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  Hammer,
  History,
  Library,
  Search,
  LineChart,
  Swords,
  Coins,
  Upload,
  Gauge,
} from "lucide-react";
import Kbd from "./Kbd";
import { cn } from "../lib/cn";

interface Route {
  to: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
}

const ROUTES: Route[] = [
  { to: "/", label: "Build a deck", hint: "Chat with the Oracle", icon: Hammer },
  { to: "/decks", label: "Saved decks", hint: "Your library", icon: Library },
  { to: "/decks/upload", label: "Upload a deck", hint: "Analyze your own list", icon: Upload },
  { to: "/playtest", label: "Run a playtest", hint: "Forge-simulated games", icon: Swords },
  { to: "/playtest/history", label: "Playtest history", hint: "Past matches", icon: History },
  { to: "/meta", label: "Meta snapshot", hint: "Archetypes + shares", icon: Gauge },
  { to: "/search", label: "Card search", hint: "Oracle text + semantic", icon: Search },
  { to: "/stats", label: "Your stats", hint: "Usage + results", icon: LineChart },
  { to: "/billing", label: "Billing", hint: "Credits + plans", icon: Coins },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ROUTES;
    return ROUTES.filter(
      (r) =>
        r.label.toLowerCase().includes(needle) ||
        r.hint?.toLowerCase().includes(needle) ||
        r.to.includes(needle),
    );
  }, [q]);

  // Reset cursor whenever the filtered list changes.
  useEffect(() => {
    setCursor(0);
  }, [q]);

  // Autofocus input and reset state when opened.
  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Global Escape + arrow-key navigation while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = items[cursor];
        if (target) {
          navigate(target.to);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, items, cursor, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 pt-[15vh] backdrop-blur-sm anim-fade-in"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-xl border border-border bg-bg-surface shadow-card-hover overflow-hidden"
      >
        <div className="flex items-center gap-3 border-b border-border/70 px-4 py-3">
          <Search size={16} className="text-fg-subtle shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to…"
            className="block w-full bg-transparent text-sm outline-none placeholder:text-fg-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>
        <ul className="max-h-80 overflow-auto py-2">
          {items.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-fg-subtle">
              No matches.
            </li>
          )}
          {items.map((r, i) => (
            <li key={r.to}>
              <Link
                to={r.to}
                onClick={onClose}
                onMouseEnter={() => setCursor(i)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 text-sm",
                  i === cursor ? "bg-bg-raised" : "hover:bg-bg-raised/60",
                )}
              >
                <r.icon
                  size={15}
                  strokeWidth={1.75}
                  className={i === cursor ? "text-fg" : "text-fg-muted"}
                />
                <span className={i === cursor ? "text-fg" : "text-fg-muted"}>
                  {r.label}
                </span>
                {r.hint && (
                  <span className="ml-2 text-[11px] text-fg-subtle">
                    {r.hint}
                  </span>
                )}
                {i === cursor && (
                  <span className="ml-auto text-fg-subtle">
                    <Kbd>↵</Kbd>
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-border/70 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
          <span>{items.length} routes</span>
          <span className="flex items-center gap-2">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            <span>navigate</span>
          </span>
        </div>
      </div>
    </div>
  );
}
